import { useState, useRef, useEffect, cloneElement } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { getEmojis, loadEmojis, Emoji } from '@twnel/web-components';
import TreeMenu from 'react-simple-tree-menu';
import {
  Wrapper, Bar, Option,
  Chevron, ItemWrapper, TestDiv,
  TreeWrapper, StyledInput, List,
  OptionPanel,
} from '../styled';
import 'react-simple-tree-menu/styles';

const OPTIONS = {
  EMOJIS: 'emojis',
  TREE: 'tree',
};

const ToggleIcon = ({ on }) => (
  <Chevron className={on ? 'rotate' : ''}>
    <i className="fas fa-chevron-right" />
  </Chevron>
);

const ListItem = ({
  level = 0,
  hasNodes,
  isOpen,
  label,
  searchTerm,
  openNodes,
  toggleNode,
  matchSearch,
  focused,
  posInSet,
  setSize,
  ...props
}) => (
  <ItemWrapper focused={focused} level={level} aria-posinset={posInSet} aria-setsize={setSize} {...props}>
    {hasNodes && (
      <TestDiv
        style={{ display: 'inline-block', width: 'auto' }}
        onClick={(e) => {
          hasNodes && toggleNode && toggleNode();
          e.stopPropagation();
        }}
      >
        <ToggleIcon on={isOpen} />
      </TestDiv>
    )}
    {label}
  </ItemWrapper>
);

ListItem.propTypes = {
  level: PropTypes.number,
  hasNodes: PropTypes.bool,
  isOpen: PropTypes.bool,
  label: PropTypes.string,
  searchTerm: PropTypes.string,
  openNodes: PropTypes.arrayOf(PropTypes.string),
  toggleNode: PropTypes.func,
  matchSearch: PropTypes.func,
  focused: PropTypes.bool,
};

const OptionsBarView = ({
  treeData,
  children,
  childrenValue = '',
  childrenRef,
  disabled = false,
  optionsVisible = false,
}) => {
  const [displayBar, setDisplayBar] = useState(false);
  const [displayPanel, setDisplayPanel] = useState(false);
  const [value, setValue] = useState(childrenValue);
  const [currentOption, setCurrentOption] = useState('');
  const panelRef = useRef(null);
  const barRef = useRef(null);
  const emojis = useSelector((state) => getEmojis(state));
  const dispatch = useDispatch();
  const refChildrenValue = useRef(childrenValue);

  useEffect(() => {
    if (childrenValue !== refChildrenValue.current) {
      setValue(childrenValue);
    }
    refChildrenValue.current = childrenValue;
  }, [childrenValue, refChildrenValue]);

  useEffect(() => {
    if (!emojis) {
      dispatch(loadEmojis(() => import(
        /* webpackChunkName: "emojis" */
        '@twnel/web-components/lib/emoji.json'
      )));
    }
  }, [emojis, dispatch]);

  const useOutsideBoundsBar = () => {
    useEffect(() => {
      const handleClickOutsideBar = (event) => {
        if (barRef.current && !barRef.current.contains(event.target)
          && displayBar && !displayPanel) {
          setDisplayBar(false);
          setCurrentOption('');
        }
      };

      document.addEventListener('mousedown', handleClickOutsideBar);
      return () => {
        document.removeEventListener('mousedown', handleClickOutsideBar);
      };
    });
  };

  const useOutsideBoundsPanel = () => {
    useEffect(() => {
      const handleClickOutsidePanel = (event) => {
        if (panelRef.current && !panelRef.current.contains(event.target)
          && barRef.current && !barRef.current.contains(event.target)
          && displayPanel) {
          setDisplayPanel(false);
          setDisplayBar(false);
          setCurrentOption('');
        }
      };

      document.addEventListener('mousedown', handleClickOutsidePanel);
      return () => {
        document.removeEventListener('mousedown', handleClickOutsidePanel);
      };
    });
  };

  useOutsideBoundsBar();
  useOutsideBoundsPanel();

  const onFocus = () => {
    setDisplayBar(true);
  };

  const onTreeOptionClick = () => {
    setDisplayPanel(true);
    setCurrentOption(OPTIONS.TREE);
  };

  const onEmojiOptionClick = () => {
    setDisplayPanel(true);
    setCurrentOption(OPTIONS.EMOJIS);
  };

  const onClickTreeItem = ({ key }) => {
    if (childrenRef && childrenRef.current && key.includes('/')) {
      const leaf = key.split('/')[1];
      childrenRef.current.maskInsert(leaf);
      setDisplayPanel(false);
    }
  };

  const onClickEmoji = (emojiValue) => {
    if (childrenRef && childrenRef.current) {
      setValue(`${value} ${emojiValue}`);
      childrenRef.current.insert(`${emojiValue}`);
    }
  };

  const treeComponent = (
    <TreeWrapper>
      <TreeMenu onClickItem={onClickTreeItem} data={treeData}>
        {({ search, items }) => (
          <>
            <StyledInput
              onChange={(e) => search(e.target.value)}
              placeholder="Type and search"
            />
            <List>
              {items.map(({ reset, key, ...props }) => (
                <ListItem key={key} {...props} />
              ))}
            </List>
          </>
        )}
      </TreeMenu>
    </TreeWrapper>
  );

  const emojisComponent = (
    <Emoji
      emojis={emojis || {
        list: [],
        map: {},
      }}
      onEmoji={onClickEmoji}
    />
  );

  const buildPanelContent = () => {
    const optionComponents = {
      [OPTIONS.EMOJIS]: emojisComponent,
      [OPTIONS.TREE]: treeComponent,
    };

    return optionComponents[currentOption] || null;
  };

  return (
    <Wrapper>
      {cloneElement(children, { onFocus })}
      {!disabled ? (
        <>
          <Bar ref={barRef} displayItem={optionsVisible || displayBar}>
            <Option isActive={currentOption === OPTIONS.EMOJIS} onClick={onEmojiOptionClick}>
              <i className="far fa-smile-beam" />
            </Option>
            <Option isActive={currentOption === OPTIONS.TREE} onClick={onTreeOptionClick}>
              <div style={{ paddingBottom: '3px' }}>{'{·}'}</div>
            </Option>
          </Bar>
          <OptionPanel ref={panelRef} displayItem={displayPanel}>
            {buildPanelContent()}
          </OptionPanel>
        </>
      ) : null}
    </Wrapper>
  );
};

OptionsBarView.propTypes = {
  treeData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  childrenValue: PropTypes.string,
  onChange: PropTypes.func,
  children: PropTypes.node.isRequired,
  childrenRef: PropTypes.shape({
    current: PropTypes.shape({
      value: PropTypes.string,
      selectionStart: PropTypes.number,
      maskInsert: PropTypes.func,
      insert: PropTypes.func,
    }),
  }).isRequired,
  disabled: PropTypes.bool,
  optionsVisible: PropTypes.bool,
};

export default OptionsBarView;
